/* ==========================================================================
   Ecoline — mega-menu.js (v2)
   Desktop category mega menus wired from sections/header.liquid via
   snippets/nav-mega-menu.liquid ([data-mega] / [data-mega-trigger] /
   [data-mega-panel]). One instance per category nav item; a module-level
   registry guarantees only ONE panel is open at a time (spec §5).
   Standalone — does not touch assets/app.js.

   The trigger is a real <a> to the collection, so click always navigates
   (works with JS off too). Opens on: hover intent (80ms in / 150ms out so
   the panel doesn't vanish while the cursor crosses the gap to it) and
   keyboard focus. Closes on: Esc (returns focus to the trigger), outside
   click, focus leaving the widget, and page scroll (keeps the panel from
   ever fighting the sticky/shrink header — see section-header.css, which
   positions the panel with `top: 100%` relative to the header itself so a
   shrink mid-open just reflows it instead of glitching; the .el-mega
   wrapper must stay NON-positioned for that anchoring to hold).
   ========================================================================== */
(function () {
  'use strict';

  var OPEN_DELAY = 80;
  var CLOSE_DELAY = 150;

  /* Registry of every instance so open() can close all the others. */
  var instances = [];

  function initMega(root) {
    if (root.__elMegaInit) return;
    root.__elMegaInit = true;
    var trigger = root.querySelector('[data-mega-trigger]');
    var panel = root.querySelector('[data-mega-panel]');
    if (!trigger || !panel) return;

    var openTimer = null;
    var closeTimer = null;
    var isOpen = false;
    /* True while close() programmatically refocuses the trigger, so the
       trigger's own focus handler doesn't immediately reopen the panel
       (Escape would otherwise be un-dismissable). */
    var suppressFocusOpen = false;

    function clearTimers() {
      if (openTimer) {
        clearTimeout(openTimer);
        openTimer = null;
      }
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
    }

    function open() {
      clearTimers();
      if (isOpen) return;
      /* Prune instances whose roots a theme-editor re-render detached, then
         close every other live panel — only one open at a time. */
      for (var i = instances.length - 1; i >= 0; i--) {
        if (!instances[i].root.isConnected) instances.splice(i, 1);
      }
      instances.forEach(function (inst) {
        if (inst.root !== root) inst.close();
      });
      isOpen = true;
      panel.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      panel.setAttribute('aria-hidden', 'false');
    }

    function close(opts) {
      clearTimers();
      if (!isOpen) return;
      isOpen = false;
      panel.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');
      if (opts && opts.focusTrigger) {
        suppressFocusOpen = true;
        trigger.focus();
        setTimeout(function () { suppressFocusOpen = false; }, 0);
      }
    }

    function scheduleOpen() {
      clearTimers();
      openTimer = setTimeout(open, OPEN_DELAY);
    }

    function scheduleClose() {
      clearTimers();
      closeTimer = setTimeout(function () { close(); }, CLOSE_DELAY);
    }

    instances.push({ root: root, close: close });

    /* Hover intent — 80ms open delay (skimming across the nav doesn't flash
       panels), 150ms close delay (moving the cursor from the trigger down
       into the panel doesn't close it). Bound only for true hover devices:
       on touch (iPad landscape hits the ≥990px nav) a tap fires mouseenter
       then click — the link just navigates, no panel flicker. */
    if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      root.addEventListener('mouseenter', scheduleOpen);
      root.addEventListener('mouseleave', scheduleClose);
    }

    /* Keyboard: focusing the trigger opens it; Esc closes and returns focus.
       (Click is left alone — the trigger is a link and navigates.) */
    trigger.addEventListener('focus', function () {
      if (!suppressFocusOpen) open();
    });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        close({ focusTrigger: true });
      }
    });

    /* Focus leaving the whole widget (trigger + panel) closes it. */
    root.addEventListener('focusout', function (e) {
      var next = e.relatedTarget;
      if (next && root.contains(next)) return;
      /* Defer so a click-driven focus change inside the panel has landed. */
      setTimeout(function () {
        if (!root.contains(document.activeElement)) close();
      }, 0);
    });

    /* Outside click closes it. */
    document.addEventListener('click', function (e) {
      if (!isOpen) return;
      if (root.contains(e.target)) return;
      close();
    });

    /* Scrolling away closes it — the panel is anchored to the header, so a
       mid-open scroll (or the sticky header's shrink transition) would
       otherwise leave it floating oddly; closing is simpler and matches
       how the drawers already behave. */
    var scrollTicking = false;
    window.addEventListener(
      'scroll',
      function () {
        if (!isOpen || scrollTicking) return;
        scrollTicking = true;
        window.requestAnimationFrame(function () {
          scrollTicking = false;
          close();
        });
      },
      { passive: true }
    );
  }

  function init() {
    document.querySelectorAll('[data-mega]').forEach(initMega);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor: a header-section re-render reinserts [data-mega] markup
     whose inline <script> tag does NOT re-execute — re-wire the fresh roots
     here (initMega's __elMegaInit guard makes this idempotent). */
  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target || !e.target.querySelectorAll) return;
    e.target.querySelectorAll('[data-mega]').forEach(initMega);
  });
})();
